/**
 * PaginationControls - Shared pagination UI for order queue tabs
 *
 * Supports two modes:
 * - Paginated: Page numbers with prev/next navigation
 * - Endless: Load More button
 *
 * Uses semantic <button> elements (not <a>) for accessibility since these
 * are client-side page changes, not navigation links.
 *
 * Extracted from OrderQueueTab and AllOrdersTab to eliminate duplication.
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button-variants';
import { generatePageNumbers } from '../utils/pagination-utils';

interface PaginationControlsProps {
  paginationMode: 'paginated' | 'endless';
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  isFetching: boolean;
  orderCount: number;
  currentOffset: number;
  ariaLabel: string;
  onPageChange: (page: number) => void;
  onLoadMore: () => void;
}

export function PaginationControls({
  paginationMode,
  currentPage,
  totalPages,
  hasMore,
  isFetching,
  orderCount,
  currentOffset,
  ariaLabel,
  onPageChange,
  onLoadMore,
}: PaginationControlsProps) {
  const { t } = useTranslation('production');
  return (
    <>
      {paginationMode === 'endless' ? (
        /* Endless scroll: Load More button */
        hasMore && (
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={onLoadMore}
              disabled={isFetching}
              className="min-w-[140px] h-12 gap-2"
            >
              {isFetching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('orders.loading')}
                </>
              ) : (
                t('orders.loadMore')
              )}
            </Button>
          </div>
        )
      ) : (
        /* Paginated: Traditional page navigation with semantic buttons */
        totalPages > 1 && (
          <nav aria-label={ariaLabel} className="mt-4">
            <div className="mx-auto flex w-full justify-center" role="navigation">
              <ul className="flex flex-row items-center gap-1">
                <li>
                  <button
                    aria-label="Go to previous page"
                    aria-disabled={currentPage === 0}
                    tabIndex={currentPage === 0 ? -1 : undefined}
                    onClick={() => currentPage > 0 && onPageChange(currentPage - 1)}
                    className={cn(
                      buttonVariants({ variant: 'ghost', size: 'default' }),
                      'gap-1 pl-2.5',
                      currentPage === 0 ? 'pointer-events-none opacity-50' : 'cursor-pointer',
                    )}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>{t('orders.previous')}</span>
                  </button>
                </li>
                {generatePageNumbers(currentPage, totalPages).map((page, idx) =>
                  page === 'ellipsis' ? (
                    <li key={`ellipsis-${idx < currentPage ? 'start' : 'end'}`}>
                      <span
                        aria-hidden
                        className="flex h-9 w-9 items-center justify-center"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">{t('orders.morePages')}</span>
                      </span>
                    </li>
                  ) : (
                    <li key={page}>
                      <button
                        aria-label={`Page ${page + 1}`}
                        aria-current={page === currentPage ? 'page' : undefined}
                        onClick={() => onPageChange(page)}
                        className={cn(
                          buttonVariants({
                            variant: page === currentPage ? 'outline' : 'ghost',
                            size: 'icon',
                          }),
                          'cursor-pointer',
                        )}
                      >
                        {page + 1}
                      </button>
                    </li>
                  ),
                )}
                <li>
                  <button
                    aria-label="Go to next page"
                    aria-disabled={currentPage >= totalPages - 1}
                    tabIndex={currentPage >= totalPages - 1 ? -1 : undefined}
                    onClick={() => currentPage < totalPages - 1 && onPageChange(currentPage + 1)}
                    className={cn(
                      buttonVariants({ variant: 'ghost', size: 'default' }),
                      'gap-1 pr-2.5',
                      currentPage >= totalPages - 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer',
                    )}
                  >
                    <span>{t('orders.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </li>
              </ul>
            </div>
          </nav>
        )
      )}

      {/* Show count indicator */}
      <div className="text-center text-sm text-muted-foreground pt-3 tabular-nums" role="status" aria-live="polite">
        {t('orders.showingCount', { count: orderCount })}
        {paginationMode === 'paginated' && totalPages > 1 && ` ${t('orders.showingCountPaginated', { page: currentPage + 1, totalPages })}`}
        {paginationMode === 'endless' && currentOffset > 0 && ` \u00B7 ${t('orders.scrolled')}`}
      </div>
    </>
  );
}
